/* eslint-disable no-console -- Model Lab intentionally emits reproducible research diagnostics. */
import React, { useEffect, useMemo, useState } from "react";
import dayjs from "lib/dayjs";
import { priceFromYield, yieldFromPrice } from "lib/bondMath";
import { dayCountFraction } from "lib/dayCount";
import SectionCard from "components/SectionCard";
import { supabase } from "services/supabaseClient";
import {
  analyzeHullWhiteBond,
  bootstrapMunicipalBenchmarkCurve,
  calibrateEffectiveHullWhiteVolatility,
  calibrateMunicipalCurveBasis,
  linearInterpolate,
  priceHullWhiteBond,
} from "pages/DebtStudio/lib/oas";

const TENORS = ["3m", "6m", ...Array.from({ length: 30 }, (_, i) => `${i + 1}y`)];
const CURVES = {
  aaaCallable: "muni-aaa-go-",
  aaaBaseline: "muni-aaa-go-base-",
};
const tenorYears = (tenor) => Number.parseFloat(tenor) / (tenor.endsWith("m") ? 12 : 1);
const numeric = (value, fallback) => Number.isFinite(Number(value)) ? Number(value) : fallback;
const roundTo = (value, decimals) => {
  const factor = 10 ** decimals;
  return Math.round((Number(value) + Number.EPSILON) * factor) / factor;
};
const format = (value, decimals = 3, suffix = "") => {
  if (!Number.isFinite(value)) return "—";
  const normalized = Math.abs(value) < 0.5 * 10 ** -decimals ? 0 : value;
  return `${normalized.toFixed(decimals)}${suffix}`;
};
const formatChange = (value, decimals = 3, suffix = "") => {
  const formatted = format(value, decimals, suffix);
  return Number.isFinite(value) && value >= 0.5 * 10 ** -decimals ? `+${formatted}` : formatted;
};
const expectedRedemptionChangeDays = (anchorDate, scenarioDate) => {
  const anchor = dayjs(anchorDate);
  const scenario = dayjs(scenarioDate);
  return anchor.isValid() && scenario.isValid() ? scenario.diff(anchor, "day") : null;
};

function Field({ label, children }) {
  return <label className="flex min-w-0 flex-col gap-1 text-xs text-muted"><span>{label}</span>{children}</label>;
}

function ComparisonRow({ label, anchor, scenario, change, emphasis = false }) {
  const className = emphasis ? "font-semibold text-app" : "text-foreground";
  return <tr className="border-t border-input">
    <th className="px-3 py-2 text-left text-xs font-medium text-muted">{label}</th>
    <td className={`px-3 py-2 text-right tabular-nums ${className}`}>{anchor}</td>
    <td className={`px-3 py-2 text-right tabular-nums ${className}`}>{scenario}</td>
    <td className={`px-3 py-2 text-right tabular-nums ${className}`}>{change}</td>
  </tr>;
}

function conventionalYields({ price, coupon, settlementDate, maturityDate, firstCallDate, callPrice, dayCount, frequency }) {
  const common = { couponRate: coupon / 100, priceTarget: price, deliveryDate: settlementDate, dayCount, frequency };
  const exactYtm = yieldFromPrice({ ...common, maturityDate }) * 100;
  const exactYtc = firstCallDate ? yieldFromPrice({ ...common, maturityDate: firstCallDate, redemption: callPrice }) * 100 : null;
  const basis = Number.isFinite(exactYtc) && exactYtc < exactYtm ? "Call" : "Maturity";
  const ytm = roundTo(exactYtm, 2);
  const ytc = Number.isFinite(exactYtc) ? roundTo(exactYtc, 2) : null;
  return { ytm, ytc, ytw: basis === "Call" ? ytc : ytm, basis };
}

function priceAtOas(inputs, oas) {
  const callable = priceHullWhiteBond({ ...inputs, spread: oas });
  const nonCallable = priceHullWhiteBond({ ...inputs, firstCallDate: null, spread: oas });
  const bump = 0.0001;
  const down = priceHullWhiteBond({ ...inputs, spread: oas - bump }).dirtyPrice;
  const up = priceHullWhiteBond({ ...inputs, spread: oas + bump }).dirtyPrice;
  return {
    cleanPrice: callable.cleanPrice,
    optionValue: callable.dirtyPrice - nonCallable.dirtyPrice,
    effectiveDuration: (down - up) / (2 * callable.dirtyPrice * bump),
    effectiveConvexity: (down + up - 2 * callable.dirtyPrice) / (callable.dirtyPrice * bump * bump),
    callDiagnostics: callable.callDiagnostics,
  };
}

function oasRiskSensitivity(inputs, oas, bumpSizesBps = [1, 2, 5, 10, 25]) {
  const base = priceHullWhiteBond({ ...inputs, spread: oas });
  return bumpSizesBps.map((bumpBps) => {
    const bump = bumpBps / 10000;
    const down = priceHullWhiteBond({ ...inputs, spread: oas - bump });
    const up = priceHullWhiteBond({ ...inputs, spread: oas + bump });
    return {
      bumpBps,
      downPrice: down.dirtyPrice,
      basePrice: base.dirtyPrice,
      upPrice: up.dirtyPrice,
      duration: (down.dirtyPrice - up.dirtyPrice) / (2 * base.dirtyPrice * bump),
      convexity: (down.dirtyPrice + up.dirtyPrice - 2 * base.dirtyPrice) / (base.dirtyPrice * bump * bump),
      downFirstCallProbability: down.callDiagnostics?.firstCallProbability,
      baseFirstCallProbability: base.callDiagnostics?.firstCallProbability,
      upFirstCallProbability: up.callDiagnostics?.firstCallProbability,
    };
  });
}

export default function OasModelLab({
  rows = [], settlementDate, marketAsOfDate, dayCount = "30/360", couponFrequency = "semiannual",
  onApplyScenario, lastAppliedScenario, onUndoScenario,
}) {
  const [open, setOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState("");
  const [inputSpreadBps, setInputSpreadBps] = useState(0);
  const [scenarioCoupon, setScenarioCoupon] = useState("4.000");
  const [firstCallDate, setFirstCallDate] = useState("");
  const [callPrice, setCallPrice] = useState(100);
  const [noticeDays, setNoticeDays] = useState(30);
  const [meanReversionPct, setMeanReversionPct] = useState("3.00");
  const [volatilityAdjustmentBps, setVolatilityAdjustmentBps] = useState(0);
  const [volatilityCalibration, setVolatilityCalibration] = useState(null);
  const [calibratingVolatility, setCalibratingVolatility] = useState(false);
  const [stepsPerYear, setStepsPerYear] = useState(12);
  const [curveData, setCurveData] = useState({ aaaCallable: [], aaaBaseline: [] });
  const [curveDates, setCurveDates] = useState({ aaaCallable: null, aaaBaseline: null });
  const [loadingCurve, setLoadingCurve] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const selectedRow = useMemo(() => rows.find((row) => row.date === selectedDate) || null, [rows, selectedDate]);
  const anchorCoupon = numeric(selectedRow?.coupon, 5);
  const maturityYears = useMemo(() => selectedDate && settlementDate ? dayCountFraction(settlementDate, selectedDate, dayCount) : null, [selectedDate, settlementDate, dayCount]);
  const benchmarkYieldRaw = useMemo(() => curveData.aaaCallable.length && Number.isFinite(maturityYears)
    ? linearInterpolate(curveData.aaaCallable.map((p) => [p.tenorYears, p.yieldRate]), maturityYears) * 100
    : null, [curveData.aaaCallable, maturityYears]);
  const benchmarkYield = Number.isFinite(benchmarkYieldRaw) ? roundTo(benchmarkYieldRaw, 2) : null;
  const aaaCallableYield = benchmarkYieldRaw;
  const currentSpreadBps = Math.round(numeric(inputSpreadBps, 0));
  const referenceYield = Number.isFinite(benchmarkYield) ? roundTo(benchmarkYield + currentSpreadBps / 100, 2) : null;
  const calibrationCurve = useMemo(() => curveData.aaaBaseline.length
    ? bootstrapMunicipalBenchmarkCurve({ points: curveData.aaaBaseline })
    : null, [curveData.aaaBaseline]);
  const modelCurve = calibrationCurve;
  const benchmarkCurveDate = curveDates.aaaCallable;
  const callableYieldByTenor = useMemo(() => Object.fromEntries(
    curveData.aaaCallable.map((point) => {
      const tenor = Number.isInteger(point.tenorYears)
        ? `${point.tenorYears}y`
        : point.tenorYears === 0.25 ? "3m" : "6m";
      return [tenor, point.yieldRate * 100];
    })
  ), [curveData.aaaCallable]);
  const curveBasis = useMemo(() => {
    if (!calibrationCurve || !settlementDate || !curveData.aaaCallable.length) return null;
    try {
      return calibrateMunicipalCurveBasis({
        curve: calibrationCurve, callableYieldByTenor, settlementDate, frequency: couponFrequency, dayCount,
      });
    } catch {
      return null;
    }
  }, [calibrationCurve, settlementDate, curveData.aaaCallable.length, callableYieldByTenor, couponFrequency, dayCount]);
  const appliedVolatilityBps = volatilityCalibration
    ? Math.max(0, Math.round(volatilityCalibration.volatilityBps) + Math.round(numeric(volatilityAdjustmentBps, 0)))
    : null;

  useEffect(() => {
    if (rows.length && (!selectedDate || !rows.some((row) => row.date === selectedDate))) setSelectedDate(rows[rows.length - 1].date);
  }, [rows, selectedDate]);

  useEffect(() => {
    if (!selectedRow) return;
    const maturity = dayjs(selectedRow.date);
    const settlement = dayjs(settlementDate);
    const inferredCall = selectedRow.call_date ? dayjs(selectedRow.call_date) : settlement.add(10, "year");
    setScenarioCoupon(numeric(selectedRow.coupon, 4).toFixed(3));
    setInputSpreadBps(Math.round(numeric(selectedRow.adjustment_bps, 0)));
    setFirstCallDate(inferredCall.isValid() && inferredCall.isBefore(maturity, "day") ? inferredCall.format("YYYY-MM-DD") : "");
    setResult(null);
  }, [selectedRow, settlementDate]);

  useEffect(() => {
    if (!open || (curveData.aaaCallable.length && curveData.aaaBaseline.length)) return;
    let mounted = true;
    const ids = Object.values(CURVES).flatMap((prefix) => TENORS.map((tenor) => `${prefix}${tenor}`));
    setLoadingCurve(true);
    setError("");
    supabase.from("market_data").select("date,security_id,tenor,value,units").in("security_id", ids)
      .lte("date", marketAsOfDate || dayjs().format("YYYY-MM-DD")).order("date", { ascending: false }).limit(2000)
      .then(({ data, error: queryError }) => {
        if (queryError) throw queryError;
        const latestById = new Map();
        for (const row of data || []) if (!latestById.has(row.security_id) && Number.isFinite(Number(row.value))) latestById.set(row.security_id, row);
        const nextData = {};
        const nextDates = {};
        for (const [name, prefix] of Object.entries(CURVES)) {
          const sourceRows = TENORS.map((tenor) => latestById.get(`${prefix}${tenor}`)).filter(Boolean);
          const curveName = name === "aaaBaseline" ? "I493" : "Callable AAA";
          if (sourceRows.length !== TENORS.length) throw new Error(`${curveName} curve is incomplete: found ${sourceRows.length} of ${TENORS.length} tenors`);
          nextData[name] = sourceRows.map((row) => ({ tenorYears: tenorYears(row.tenor), yieldRate: Number(row.value) > 1 ? Number(row.value) / 100 : Number(row.value) }));
          nextDates[name] = sourceRows.reduce((latest, row) => row.date > latest ? row.date : latest, "");
        }
        if (mounted) { setCurveData(nextData); setCurveDates(nextDates); }
      })
      .catch((queryError) => mounted && setError(String(queryError?.message || queryError)))
      .finally(() => mounted && setLoadingCurve(false));
    return () => { mounted = false; };
  }, [open, curveData.aaaCallable.length, curveData.aaaBaseline.length, marketAsOfDate]);

  useEffect(() => {
    if (!open || !calibrationCurve || !curveBasis || !selectedRow || !Number.isFinite(aaaCallableYield)) return;
    let mounted = true;
    setCalibratingVolatility(true);
    setVolatilityCalibration(null);
    const timer = window.setTimeout(() => {
      try {
        const calibration = calibrateEffectiveHullWhiteVolatility({
          curve: calibrationCurve,
          settlementDate,
          maturityDate: selectedRow.date,
          callableYieldPct: aaaCallableYield,
          curveBasis: curveBasis.spread,
          meanReversion: numeric(meanReversionPct, 3) / 100,
          stepsPerYear: numeric(stepsPerYear, 12),
          frequency: couponFrequency,
          dayCount,
        });
        if (mounted) setVolatilityCalibration(calibration);
      } catch (calibrationError) {
        if (mounted) setError(String(calibrationError?.message || calibrationError));
      } finally {
        if (mounted) setCalibratingVolatility(false);
      }
    }, 0);
    return () => { mounted = false; window.clearTimeout(timer); };
  }, [open, calibrationCurve, curveBasis, selectedRow, aaaCallableYield, meanReversionPct, stepsPerYear, settlementDate, couponFrequency, dayCount]);

  const inputClass = "h-8 rounded-md border border-input bg-background px-2 text-sm text-foreground shadow-sm";
  const calculate = () => {
    setError(""); setResult(null);
    if (!selectedRow) return setError("Select a maturity.");
    if (!modelCurve || !Number.isFinite(benchmarkYield)) return setError("The required market curves are not available yet.");
    if (!Number.isFinite(appliedVolatilityBps)) return setError("The selected-maturity volatility calibration is not available yet.");
    if (!firstCallDate) return setError("Enter a first call date before maturity.");
    setCalculating(true);
    window.setTimeout(() => {
      try {
        const coupon = numeric(scenarioCoupon, anchorCoupon);
        const redemption = numeric(callPrice, 100);
        const yieldToPriceDate = anchorCoupon > referenceYield ? firstCallDate : selectedRow.date;
        const anchorPrice = priceFromYield({ couponRate: anchorCoupon / 100, yieldRate: referenceYield / 100, deliveryDate: settlementDate,
          maturityDate: yieldToPriceDate, redemption: yieldToPriceDate === firstCallDate ? redemption : 100, dayCount, frequency: couponFrequency });
        const curve = modelCurve;
        const common = { curve, settlementDate, maturityDate: selectedRow.date, firstCallDate, callPrice: redemption,
          noticeDays: numeric(noticeDays, 30), meanReversion: numeric(meanReversionPct, 3) / 100, volatility: appliedVolatilityBps / 10000,
          stepsPerYear: numeric(stepsPerYear, 12), frequency: couponFrequency, dayCount };
        const anchorAnalysis = analyzeHullWhiteBond({ ...common, couponRate: anchorCoupon, cleanPrice: anchorPrice });
        const scenarioAnalysis = priceAtOas({ ...common, couponRate: coupon }, anchorAnalysis.oas);
        const anchorRiskSensitivity = oasRiskSensitivity({ ...common, couponRate: anchorCoupon }, anchorAnalysis.oas);
        const scenarioRiskSensitivity = oasRiskSensitivity({ ...common, couponRate: coupon }, anchorAnalysis.oas);
        const yieldInputs = { settlementDate, maturityDate: selectedRow.date, firstCallDate, callPrice: redemption, dayCount, frequency: couponFrequency };
        const anchorYields = conventionalYields({ ...yieldInputs, price: anchorPrice, coupon: anchorCoupon });
        const scenarioYields = conventionalYields({ ...yieldInputs, price: scenarioAnalysis.cleanPrice, coupon });
        const forcedCallPrice = (forcedCoupon) => priceFromYield({
          couponRate: forcedCoupon / 100,
          yieldRate: referenceYield / 100,
          deliveryDate: settlementDate,
          maturityDate: firstCallDate,
          redemption,
          dayCount,
          frequency: couponFrequency,
        });
        const anchorForcedCallPrice = forcedCallPrice(anchorCoupon);
        const scenarioForcedCallPrice = forcedCallPrice(coupon);
        const nextResult = {
          anchor: { ...anchorAnalysis, ...anchorYields, coupon: anchorCoupon, cleanPrice: anchorPrice, forcedCallPrice: anchorForcedCallPrice, survivalValue: anchorPrice - anchorForcedCallPrice, effectiveSpreadBps: Math.round((anchorYields.ytw - benchmarkYield) * 100), riskSensitivity: anchorRiskSensitivity },
          scenario: { ...scenarioAnalysis, ...scenarioYields, coupon, oasBps: anchorAnalysis.oasBps, forcedCallPrice: scenarioForcedCallPrice, survivalValue: scenarioAnalysis.cleanPrice - scenarioForcedCallPrice, effectiveSpreadBps: Math.round((scenarioYields.ytw - benchmarkYield) * 100), riskSensitivity: scenarioRiskSensitivity },
        };
        setResult(nextResult);

        console.groupCollapsed(`[OAS Model Lab] ${selectedRow.date}: ${anchorCoupon.toFixed(3)}% → ${coupon.toFixed(3)}%`);
        console.log("Inputs", {
          settlementDate,
          maturityDate: selectedRow.date,
          firstCallDate,
          callPrice: redemption,
          benchmarkCurveId: "muni-aaa-go",
          benchmarkCurveLabel: "Muni AAA GO",
          benchmarkYieldPct: benchmarkYield,
          aaaCallableCalibrationYieldPct: aaaCallableYield,
          currentSpreadBps,
          currentReferenceYieldPct: referenceYield,
          meanReversionPct: numeric(meanReversionPct, 3),
          calibratedHullWhiteVolatilityBps: volatilityCalibration?.volatilityBps,
          volatilityAdjustmentBps: numeric(volatilityAdjustmentBps, 0),
          appliedHullWhiteVolatilityBps: appliedVolatilityBps,
          curveBasisBps: curveBasis?.spreadBps,
          noticeDays: numeric(noticeDays, 30),
          stepsPerYear: numeric(stepsPerYear, 12),
          benchmarkCurveAsOf: curveDates.aaaCallable,
          aaaCallableCurveAsOf: curveDates.aaaCallable,
          i493AsOf: curveDates.aaaBaseline,
        });
        console.table([
          {
            case: "Current Anchor",
            couponPct: nextResult.anchor.coupon,
            conventionalSpreadBps: nextResult.anchor.effectiveSpreadBps,
            oasBps: nextResult.anchor.oasBps,
            cleanPrice: nextResult.anchor.cleanPrice,
            ytmPct: nextResult.anchor.ytm,
            ytcPct: nextResult.anchor.ytc,
            ytwPct: nextResult.anchor.ytw,
            ytwBasis: nextResult.anchor.basis,
            optionValue: nextResult.anchor.optionValue,
            oasSpreadDuration: nextResult.anchor.effectiveDuration,
            oasSpreadConvexity: nextResult.anchor.effectiveConvexity,
          },
          {
            case: "Scenario",
            couponPct: nextResult.scenario.coupon,
            conventionalSpreadBps: nextResult.scenario.effectiveSpreadBps,
            oasBps: nextResult.scenario.oasBps,
            cleanPrice: nextResult.scenario.cleanPrice,
            ytmPct: nextResult.scenario.ytm,
            ytcPct: nextResult.scenario.ytc,
            ytwPct: nextResult.scenario.ytw,
            ytwBasis: nextResult.scenario.basis,
            optionValue: nextResult.scenario.optionValue,
            oasSpreadDuration: nextResult.scenario.effectiveDuration,
            oasSpreadConvexity: nextResult.scenario.effectiveConvexity,
          },
        ]);
        console.table(anchorRiskSensitivity.map((row, index) => ({
          bumpBps: row.bumpBps,
          anchorDuration: row.duration,
          anchorConvexity: row.convexity,
          scenarioDuration: scenarioRiskSensitivity[index].duration,
          scenarioConvexity: scenarioRiskSensitivity[index].convexity,
          anchorCallProbabilityDownPct: row.downFirstCallProbability * 100,
          anchorCallProbabilityBasePct: row.baseFirstCallProbability * 100,
          anchorCallProbabilityUpPct: row.upFirstCallProbability * 100,
          scenarioCallProbabilityDownPct: scenarioRiskSensitivity[index].downFirstCallProbability * 100,
          scenarioCallProbabilityBasePct: scenarioRiskSensitivity[index].baseFirstCallProbability * 100,
          scenarioCallProbabilityUpPct: scenarioRiskSensitivity[index].upFirstCallProbability * 100,
        })));
        console.table([
          {
            case: "Current Anchor",
            firstCallProbabilityPct: nextResult.anchor.callDiagnostics?.firstCallProbability * 100,
            callWithin1YearPct: nextResult.anchor.callDiagnostics?.callWithinOneYearProbability * 100,
            callWithin3YearsPct: nextResult.anchor.callDiagnostics?.callWithinThreeYearsProbability * 100,
            callWithin5YearsPct: nextResult.anchor.callDiagnostics?.callWithinFiveYearsProbability * 100,
            eventualCallProbabilityPct: nextResult.anchor.callDiagnostics?.eventualCallProbability * 100,
            maturityProbabilityPct: nextResult.anchor.callDiagnostics?.maturityProbability * 100,
            expectedRedemptionDate: nextResult.anchor.callDiagnostics?.expectedRedemptionDate,
            expectedAverageLifeYears: nextResult.anchor.callDiagnostics?.expectedRedemptionYears,
            forcedFirstCallPrice: nextResult.anchor.forcedCallPrice,
            constantOasPrice: nextResult.anchor.cleanPrice,
            modelLessForcedCallPrice: nextResult.anchor.survivalValue,
          },
          {
            case: "Scenario",
            firstCallProbabilityPct: nextResult.scenario.callDiagnostics?.firstCallProbability * 100,
            callWithin1YearPct: nextResult.scenario.callDiagnostics?.callWithinOneYearProbability * 100,
            callWithin3YearsPct: nextResult.scenario.callDiagnostics?.callWithinThreeYearsProbability * 100,
            callWithin5YearsPct: nextResult.scenario.callDiagnostics?.callWithinFiveYearsProbability * 100,
            eventualCallProbabilityPct: nextResult.scenario.callDiagnostics?.eventualCallProbability * 100,
            maturityProbabilityPct: nextResult.scenario.callDiagnostics?.maturityProbability * 100,
            expectedRedemptionDate: nextResult.scenario.callDiagnostics?.expectedRedemptionDate,
            expectedAverageLifeYears: nextResult.scenario.callDiagnostics?.expectedRedemptionYears,
            forcedFirstCallPrice: nextResult.scenario.forcedCallPrice,
            constantOasPrice: nextResult.scenario.cleanPrice,
            modelLessForcedCallPrice: nextResult.scenario.survivalValue,
          },
        ]);
        console.log("Raw result", nextResult);
        console.groupEnd();
      } catch (calculationError) { setError(String(calculationError?.message || calculationError)); }
      finally { setCalculating(false); }
    }, 0);
  };
  const delta = (key) => result ? result.scenario[key] - result.anchor[key] : null;

  return <SectionCard className="mt-5" title="Coupon Spread Analyzer" subtitle="Estimate the conventional spread for a different coupon while preserving modeled option-adjusted spread (OAS) economics."
    headerAction={<button type="button" className="btn btn-primary" onClick={() => setOpen((value) => !value)}>{open ? "Hide" : "Open"}</button>}>
    {open ? <div className="space-y-4">
      <div className="rounded-md border border-yellow-300 bg-yellow-50 px-3 py-2 text-xs text-yellow-900 dark:border-yellow-800 dark:bg-yellow-950/30 dark:text-yellow-100">
        The current coupon and spread create the anchor price; calibrated OAS is then held constant when the coupon changes. Nothing is written to the scale until Apply is selected.
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6">
        <Field label="Maturity"><select className={inputClass} value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)}>{rows.map((row) => <option key={row.date} value={row.date}>{dayjs(row.date).format("M/D/YYYY")}</option>)}</select></Field>
        <Field label="Current coupon (%)"><input className={`${inputClass} bg-muted/30`} readOnly value={format(anchorCoupon, 3)} /></Field>
        <Field label="Muni AAA GO yield (%)"><input className={`${inputClass} bg-muted/30`} readOnly value={format(benchmarkYield, 2)} /></Field>
        <Field label="Current spread (bps)"><input className={inputClass} type="number" step="1" value={inputSpreadBps} onChange={(e) => { setInputSpreadBps(e.target.value); setResult(null); }} onBlur={() => setInputSpreadBps(Math.round(numeric(inputSpreadBps, 0)))} /></Field>
        <Field label="Current reference yield (%)"><input className={`${inputClass} bg-muted/30`} readOnly value={format(referenceYield, 2)} /></Field>
        <Field label="Scenario coupon (%)"><input className={inputClass} type="number" step="0.125" value={scenarioCoupon} onChange={(e) => { setScenarioCoupon(e.target.value === "" ? "" : numeric(e.target.value, 0).toFixed(3)); setResult(null); }} onBlur={() => setScenarioCoupon(numeric(scenarioCoupon, 0).toFixed(3))} /></Field>
        <Field label="First call date"><input className={inputClass} type="date" value={firstCallDate} onChange={(e) => { setFirstCallDate(e.target.value); setResult(null); }} /></Field>
      </div>
      <details className="rounded-md border border-input px-3 py-2"><summary className="cursor-pointer text-sm font-medium text-app">Advanced model assumptions</summary>
        <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7">
          <Field label="Call price"><input className={inputClass} type="number" step="0.001" value={callPrice} onChange={(e) => { setCallPrice(e.target.value); setResult(null); }} /></Field>
          <Field label="Notice days"><input className={inputClass} type="number" step="1" value={noticeDays} onChange={(e) => { setNoticeDays(e.target.value); setResult(null); }} /></Field>
          <Field label="Mean reversion"><select className={inputClass} value={meanReversionPct} onChange={(e) => { setMeanReversionPct(e.target.value); setVolatilityAdjustmentBps(0); setResult(null); }}><option value="1.00">1.00%</option><option value="3.00">3.00%</option><option value="5.00">5.00%</option></select></Field>
          <Field label="Model-implied vol (bps)"><input className={`${inputClass} bg-muted/30`} readOnly value={calibratingVolatility ? "Calibrating..." : format(volatilityCalibration?.volatilityBps, 0)} /></Field>
          <Field label="Vol adjustment (bps)"><input className={inputClass} type="number" step="1" value={volatilityAdjustmentBps} onChange={(e) => { setVolatilityAdjustmentBps(e.target.value); setResult(null); }} onBlur={() => setVolatilityAdjustmentBps(Math.round(numeric(volatilityAdjustmentBps, 0)))} /></Field>
          <Field label="Applied effective vol (bps)"><input className={`${inputClass} bg-muted/30`} readOnly value={format(appliedVolatilityBps, 0)} /></Field>
          <Field label="Lattice steps/year"><select className={inputClass} value={stepsPerYear} onChange={(e) => { setStepsPerYear(Number(e.target.value)); setResult(null); }}><option value={6}>6</option><option value={12}>12</option><option value={24}>24</option><option value={48}>48</option></select></Field>
        </div>
      </details>
      <div className="flex flex-wrap items-center gap-3">
        <button type="button" className="btn btn-primary" onClick={calculate} disabled={loadingCurve || calibratingVolatility || calculating || !volatilityCalibration}>{calculating ? "Calculating..." : calibratingVolatility ? "Calibrating..." : "Run Coupon Scenario"}</button>
        <span className="text-xs text-muted">Muni AAA GO {benchmarkCurveDate ? `as of ${dayjs(benchmarkCurveDate).format("M/D/YYYY")}` : "loading..."}; AAA volatility calibration {curveDates.aaaCallable && curveDates.aaaBaseline ? `as of ${dayjs(curveDates.aaaCallable).format("M/D/YYYY")}` : "loading..."}</span>
      </div>
      {error ? <div className="text-sm text-red-500">{error}</div> : null}
      {lastAppliedScenario ? <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-input bg-muted/20 px-3 py-2">
        <span className="text-xs text-muted">Last Apply changed {dayjs(lastAppliedScenario.date).format("M/D/YYYY")} from {lastAppliedScenario.coupon.toFixed(3)}% / {lastAppliedScenario.spreadBps} bps.</span>
        <button type="button" className="btn" onClick={onUndoScenario} disabled={!onUndoScenario}>
          Undo: restore {lastAppliedScenario.coupon.toFixed(3)}% / {lastAppliedScenario.spreadBps} bps
        </button>
      </div> : null}
      {result ? <>
        <div className="flex flex-wrap items-center justify-between gap-4 rounded-md border border-primary/30 bg-primary/5 px-4 py-3">
          <div>
            <div className="text-xs font-medium uppercase tracking-wide text-muted">Indicated conventional spread</div>
            <div className="mt-0.5 text-xl font-semibold tabular-nums text-app">{Math.round(result.scenario.effectiveSpreadBps)} bps <span className="text-sm font-normal text-muted">at {result.scenario.coupon.toFixed(3)}% coupon</span></div>
            <div className="mt-0.5 text-xs text-muted">Constant-OAS change of {formatChange(delta("effectiveSpreadBps"), 0, " bps")} from the current scale.</div>
          </div>
          <button type="button" className="btn btn-primary" onClick={() => onApplyScenario?.({ date: selectedDate, coupon: Number(result.scenario.coupon.toFixed(3)), spreadBps: Math.round(result.scenario.effectiveSpreadBps) })} disabled={!onApplyScenario}>
            Apply to {dayjs(selectedDate).format("M/D/YYYY")}
          </button>
        </div>
        <div className="overflow-x-auto rounded-md border border-input"><table className="w-full min-w-[680px] text-sm">
        <thead className="bg-muted/30 text-xs text-muted"><tr><th className="px-3 py-2 text-left">Metric</th><th className="px-3 py-2 text-right">Current Anchor</th><th className="px-3 py-2 text-right">Scenario</th><th className="px-3 py-2 text-right">Change</th></tr></thead>
        <tbody>
          <ComparisonRow label="Coupon" anchor={format(result.anchor.coupon, 3, "%")} scenario={format(result.scenario.coupon, 3, "%")} change={formatChange(delta("coupon"), 3, "%")} />
          <ComparisonRow label="Muni AAA GO yield" anchor={format(benchmarkYield, 2, "%")} scenario={format(benchmarkYield, 2, "%")} change="0.00%" />
          <ComparisonRow label="Conventional YTW spread" anchor={format(result.anchor.effectiveSpreadBps, 0, " bps")} scenario={format(result.scenario.effectiveSpreadBps, 0, " bps")} change={formatChange(delta("effectiveSpreadBps"), 0, " bps")} emphasis />
          <ComparisonRow label="Clean price" anchor={format(result.anchor.cleanPrice)} scenario={format(result.scenario.cleanPrice)} change={formatChange(delta("cleanPrice"))} />
          <ComparisonRow label="Yield to maturity" anchor={format(result.anchor.ytm, 2, "%")} scenario={format(result.scenario.ytm, 2, "%")} change={formatChange(delta("ytm") * 100, 0, " bps")} />
          <ComparisonRow label="Yield to first call" anchor={format(result.anchor.ytc, 2, "%")} scenario={format(result.scenario.ytc, 2, "%")} change={formatChange(delta("ytc") * 100, 0, " bps")} />
          <ComparisonRow label="Yield to worst" anchor={format(result.anchor.ytw, 2, "%")} scenario={format(result.scenario.ytw, 2, "%")} change={formatChange(delta("ytw") * 100, 0, " bps")} />
          <ComparisonRow label="YTW basis" anchor={result.anchor.basis} scenario={result.scenario.basis} change={result.anchor.basis === result.scenario.basis ? "No change" : `${result.anchor.basis} → ${result.scenario.basis}`} />
          <ComparisonRow label="Calibrated OAS (held constant)" anchor={format(result.anchor.oasBps, 0, " bps")} scenario={format(result.scenario.oasBps, 0, " bps")} change="0 bps" />
          <ComparisonRow label="Embedded option value" anchor={format(result.anchor.optionValue)} scenario={format(result.scenario.optionValue)} change={formatChange(delta("optionValue"))} />
          <ComparisonRow label="Probability of call at first call" anchor={format(result.anchor.callDiagnostics?.firstCallProbability * 100, 1, "%")} scenario={format(result.scenario.callDiagnostics?.firstCallProbability * 100, 1, "%")} change={formatChange((result.scenario.callDiagnostics?.firstCallProbability - result.anchor.callDiagnostics?.firstCallProbability) * 100, 1, "%")} />
          <ComparisonRow label="Probability of call before maturity" anchor={format(result.anchor.callDiagnostics?.eventualCallProbability * 100, 1, "%")} scenario={format(result.scenario.callDiagnostics?.eventualCallProbability * 100, 1, "%")} change={formatChange((result.scenario.callDiagnostics?.eventualCallProbability - result.anchor.callDiagnostics?.eventualCallProbability) * 100, 1, "%")} />
          <ComparisonRow label="Expected redemption date" anchor={result.anchor.callDiagnostics?.expectedRedemptionDate ? dayjs(result.anchor.callDiagnostics.expectedRedemptionDate).format("M/D/YYYY") : "—"} scenario={result.scenario.callDiagnostics?.expectedRedemptionDate ? dayjs(result.scenario.callDiagnostics.expectedRedemptionDate).format("M/D/YYYY") : "—"} change={formatChange(expectedRedemptionChangeDays(result.anchor.callDiagnostics?.expectedRedemptionDate, result.scenario.callDiagnostics?.expectedRedemptionDate), 0, " days")} />
          <ComparisonRow label="Expected average life" anchor={format(result.anchor.callDiagnostics?.expectedRedemptionYears, 2, " yrs")} scenario={format(result.scenario.callDiagnostics?.expectedRedemptionYears, 2, " yrs")} change={formatChange((result.scenario.callDiagnostics?.expectedRedemptionYears - result.anchor.callDiagnostics?.expectedRedemptionYears), 2, " yrs")} />
        </tbody>
      </table></div>
      </> : null}
    </div> : null}
  </SectionCard>;
}
